import { useMemo, useState } from 'react'
import { useApplication } from 'src/context/application'
import {
  ApplicationBodyContainer, 
  ApplicationBodyContent,
  DefaultTitle, 
  DefaultSubtitle,
} from 'src/components/application/defaultComponents'
import { FormInputErrorMessage } from 'src/components/application/formComponents/styles'
import ApplicationPrebuiltFooter from 'src/components/application/PrebuiltBodyFooter'
import { subareasOfPractice, lookupAOPCode } from 'src/constants/application'
import PracticeForm from 'src/components/application/mainPagesComponents/Firm/PracticeForm'

const Practice = () => {
  const { application } = useApplication()
  const [scrollErrorMessage, setScrollErrorMessage] = useState(false)
  const [selectedPracticeAreas, setSelectedPracticeAreas] = useState<string[]>(() => 
    (!!application?.firmSection?.practiceAreas) ? 
      [...application.firmSection.practiceAreas.map(pArea => pArea?.area ?? '').filter(pArea => !!pArea)] : 
      []
  )
  const [selectedPracticeSubAreas, setSelectedPracticeSubAreas] = useState(() => 
    (!!application?.firmSection?.practiceAreas) ? 
      [...application.firmSection.practiceAreas.flatMap(ar => ar.types && ar.types.map(tp => ({ area: ar.area, subArea: tp.type})))] : 
      []
  )
  const [otherInput, setOtherInput] = useState(() => {
    const otherArea = application?.firmSection?.practiceAreas?.find(ar => ar.area === 'Other')
    if(otherArea) return otherArea?.types?.[0].type ?? ''
    return ''
})
  const [utilizePatentAgents, setUtilizePatentAgents] = useState(() => {
    const intPropArea = application?.firmSection?.practiceAreas?.find(ar => ar.area === 'Intellectual Property')
    if(intPropArea) return intPropArea?.hasPatentAgents ?? ''
    return ''
  })
  const [howManyAgents, setHowManyAgents] = useState(() => {
    const intPropArea = application?.firmSection?.practiceAreas?.find(ar => ar.area === 'Intellectual Property')
    if(intPropArea) return intPropArea?.howManyPatentAgents ?? ''
    return ''
  })


  const isAreaSelected = (area: string) => selectedPracticeAreas.includes(area)
  
  const isSubAreaSelected = (subArea: string) => selectedPracticeSubAreas.some(search => search?.subArea === subArea)

  const handleAreaSelect = (area: string) => {
    if(isAreaSelected(area)) {
      setSelectedPracticeAreas(prev => prev.filter(searchArea => searchArea !== area))
      setSelectedPracticeSubAreas(prev => prev.filter(search => search?.area !== area))
    } else {
      setSelectedPracticeAreas(prev => ([ ...prev, area ]))
    }
  }

  const handleSubAreaSelect = (area: string, subArea: string) => {
    if(isSubAreaSelected(subArea)) {
      setSelectedPracticeSubAreas(prev => prev.filter(searchArea => searchArea?.subArea !== subArea))
    } else {
      setSelectedPracticeSubAreas(prev => ([ ...prev, { area, subArea } ]))
    }
  }

  const practiceAreasBuilder = () => {
    return selectedPracticeAreas.map(area => {
      const currArea = application?.firmSection?.practiceAreas?.find(pAr => pAr.area === area)
      let returnArea = {
        area,
        percentage: currArea?.percentage ?? 0,
        code: lookupAOPCode(area),
        types: 
        area === 'Other' ?
          [{
            type: otherInput.trim(),
            percentage: currArea?.types?.find(tp => tp.type === otherInput.trim())?.percentage ?? 0,
            code: "OT"
          }] :
          selectedPracticeSubAreas
          .filter(sub => sub?.area === area)
          .map(sub => ({ 
            type: sub?.subArea,  
            percentage: currArea?.types?.find(tp => tp.type === sub?.subArea)?.percentage ?? 0,
            code: lookupAOPCode(sub?.subArea)
          }))
      }
      if(area === 'Intellectual Property') {
        return {
          ...returnArea,
          hasPatentAgents: utilizePatentAgents,
          howManyPatentAgents: Number(howManyAgents)
        }
      }
      return returnArea
    })
  }

  const validateAreasSelected = () => {
    if(selectedPracticeAreas.length === 0) return false

    if(selectedPracticeAreas.includes('Intellectual Property')) {
      if(!utilizePatentAgents) return false

      if(utilizePatentAgents === 'Yes' && !howManyAgents) return false
    }

    let isValid = true
    selectedPracticeAreas.forEach(area => {
      if(area === 'Other') {
        if(!otherInput) isValid = false
      } else {
        if(!!subareasOfPractice[area]) {
          if(!selectedPracticeSubAreas.some(sub => sub?.area === area)) {
            isValid = false
          }
        }
      }

      if(!!subareasOfPractice[area]) {
        if(!selectedPracticeSubAreas.some(sub => sub?.area === area)) {
          isValid = false
        }
      }
    })

    if(isValid === true && scrollErrorMessage === true) {
      setScrollErrorMessage(false)
    }
    
    return isValid
  }

  const updatedApplicationData = () => ({
    ...application,
    currentPage: window.location.pathname,
    firmSection: { ...application.firmSection, isNewPracticeAreas : true , practiceAreas: practiceAreasBuilder() } 
  })




  const hasSubAreas = useMemo(() => {
    return selectedPracticeAreas.some((area) => 
     !!Object.keys(subareasOfPractice).includes(area)
    )
  }, [selectedPracticeAreas])

  const handleValidateBeforeSubmit = () => {
    if(!validateAreasSelected()) {
      setScrollErrorMessage(true)
      let elementId = 'practice__error_message'
      if(hasSubAreas) {
        elementId = 'practiceform__error_message'
      }
      document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='75%'>
        <DefaultTitle id="practice__error_message">Please select the areas of practice that apply to your firm.</DefaultTitle>
        <DefaultSubtitle>
          Please select all that apply.
        </DefaultSubtitle>

        {scrollErrorMessage && !hasSubAreas ? (
          <FormInputErrorMessage style={{ marginBottom: '24px' }}>
            You must select at least 1 area of practice
          </FormInputErrorMessage>
        ) : null}
        <PracticeForm 
          handleAreaSelect={handleAreaSelect}
          isAreaSelected={isAreaSelected}
          otherInput={otherInput}
          setOtherInput={setOtherInput}
          handleSubAreaSelect={handleSubAreaSelect}
          isSubAreaSelected={isSubAreaSelected}
          utilizePatentAgents={utilizePatentAgents}
          setUtilizePatentAgents={setUtilizePatentAgents}
          howManyAgents={howManyAgents}
          setHowManyAgents={setHowManyAgents}
          isSubAreaInvalid={scrollErrorMessage}
        />
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute="/firm/main"
        isFormValid={validateAreasSelected()}
        nextRoute="/firm/percentages"
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={handleValidateBeforeSubmit}
      />

    </ApplicationBodyContainer>
  )
}

export default Practice